import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { aboutData, brand } from "../mock";

export default function About() {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 30]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-6, 6]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.97]);

  return (
    <section id="about" ref={ref} className="relative py-14 lg:py-20">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Visual side with parallax */}
          <motion.div
            style={{ y: y1, scale }}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border border-[#E5E5E5] bg-white soft-shadow">
                <div className="aspect-[4/5] bg-gradient-to-br from-white via-[#fbf7f7] to-[#f5eeee] relative">
                  <div className="absolute inset-0 dot-pattern opacity-30" />
                  {/* Animated radial rings */}
                  <div className="absolute inset-0 grid place-items-center">
                    <motion.div style={{ rotate }} className="relative w-60 h-60">
                      <div className="absolute inset-0 rounded-full border border-[#73100D]/25 spin-slow" />
                      <div className="absolute inset-6 rounded-full border border-[#98413F]/25 spin-reverse" />
                      <div className="absolute inset-12 rounded-full border border-[#73100D]/15" />
                      <div className="absolute inset-16 rounded-2xl bg-white grid place-items-center brand-glow border border-[#E5E5E5]">
                        <img src={brand.logo} alt="RSP" className="w-24 h-24 object-contain" />
                      </div>
                      <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#73100D] pulse-soft" />
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#98413F] pulse-soft" />
                      <span className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#5E0000] pulse-soft" />
                      <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#98413F] pulse-soft" />
                    </motion.div>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 bg-white rounded-xl p-4 border border-[#E5E5E5] soft-shadow">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#73100D] font-semibold font-mono">Engineering since 2016 · AI · Automation · Enterprise Software</div>
                    <div className="text-[#111] font-semibold mt-1 text-sm">From traditional software to AI-first systems</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text side */}
          <motion.div
            style={{ y: y2 }}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" /> {aboutData.pill}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight text-[#111]">
              {aboutData.title}{" "}
              <span className="brand-gradient-text">{aboutData.highlight}</span>{" "}
              {aboutData.titleTail}
            </h2>
            <p className="mt-5 text-[#73100D] font-semibold text-sm uppercase tracking-[0.18em]">{aboutData.subtitle}</p>
            <p className="mt-4 text-[#555] max-w-2xl leading-relaxed">{aboutData.description}</p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {aboutData.pillars.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1, duration: 0.7 }}
                  className="rounded-2xl border border-[#E5E5E5] bg-white p-6 transition-all duration-500 hover:border-[#73100D]/30 hover:-translate-y-1 hover:shadow-[0_18px_50px_-18px_rgba(115,16,13,0.18)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#73100D]/10 grid place-items-center">
                      <CheckCircle2 className="w-5 h-5 text-[#73100D]" />
                    </div>
                    <h4 className="text-base font-semibold text-[#111]">{f.title}</h4>
                  </div>
                  <p className="mt-3 text-sm text-[#555] leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>

            {aboutData.missionVision && (
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.7 }}
                  className="relative rounded-2xl border border-[#73100D]/20 bg-gradient-to-br from-[#73100D] to-[#5E0000] p-6 text-white overflow-hidden"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#E5C7C8] font-mono font-semibold">Our Mission</div>
                  <p className="mt-3 text-base font-semibold leading-relaxed">{aboutData.missionVision.mission}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.7 }}
                  className="relative rounded-2xl border border-[#E5E5E5] bg-white p-6 overflow-hidden"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#73100D]/5 blur-2xl" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#73100D] font-mono font-semibold">Our Vision</div>
                  <p className="mt-3 text-base font-semibold text-[#111] leading-relaxed">{aboutData.missionVision.vision}</p>
                </motion.div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
